import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import type { DiscoverPromoSlide } from "@shared/discoverPromoSlide";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { DEFAULT_DISCOVER_PROMO_SLIDES } from "../DiscoverPage.constants";

function mergePromoSlides(apiSlides: DiscoverPromoSlide[]): DiscoverPromoSlide[] {
  const apiIds = new Set(apiSlides.map((s) => s.id));
  const constants = DEFAULT_DISCOVER_PROMO_SLIDES.filter((s) => !apiIds.has(s.id));
  return [...apiSlides, ...constants];
}

export function useDiscoverPromoSlides(enabled: boolean) {
  const [apiSlides, setApiSlides] = useState<DiscoverPromoSlide[]>([]);
  const [loading, setLoading] = useState(() => enabled);

  /** Before paint: avoid one frame with loading=false while enabled (prevents carousel mounting then reflowing). */
  useLayoutEffect(() => {
    if (!enabled) {
      setApiSlides([]);
      setLoading(false);
      return;
    }
    setLoading(true);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        const response = await trpc.discover.listPublicPromoSlides.query();
        if (!cancelled) {
          setApiSlides(response.slides ?? []);
        }
      } catch (err) {
        if (!cancelled) {
          handleTrpcError(err);
          setApiSlides([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const slides = useMemo(() => mergePromoSlides(apiSlides), [apiSlides]);

  return { slides, loading };
}
